import { useState } from "react";
import { MailWarning, X, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { useEmailVerificationResend } from "@/hooks/useEmailVerificationResend";

/**
 * Bannière affichée si l'utilisateur connecté n'a pas encore vérifié son email.
 * Se cache automatiquement pour la session courante si l'utilisateur clique sur ×.
 */
export function EmailVerificationBanner() {
  const { user } = useAuth();
  const { t } = useLang();
  const [hidden, setHidden] = useState(false);
  const { cooldown, sending, canResend, resend, providerIssues, sandboxMode, sandboxMessage } = useEmailVerificationResend({
    syncStatus: true,
  });

  if (!user || user.email_verified || hidden) return null;

  return (
    <div
      data-testid="email-verify-banner"
      className="flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3 sm:flex-row sm:items-center md:p-4"
    >
      <div className="h-9 w-9 shrink-0 rounded-lg bg-amber-500 text-white flex items-center justify-center">
        <MailWarning className="h-4 w-4" strokeWidth={2.25} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-amber-900 text-sm">
          {t("verify.banner_title")}
        </div>
        <div className="text-xs text-amber-800 mt-0.5">
          {t("verify.confirm_email")} <span className="font-mono font-semibold">{user.email}</span>
          {sandboxMode ? (
            <span className="block mt-1 font-semibold text-amber-950">
              ⚠ Mode sandbox Resend : seule l&apos;adresse du compte Resend reçoit les e-mails de test.
              {sandboxMessage ? ` ${sandboxMessage}` : ""}
            </span>
          ) : providerIssues?.length > 0 ? (
            <span className="block mt-1 text-amber-900/90">
              ⚠ {providerIssues[0]}
            </span>
          ) : (
            <span className="block mt-0.5 truncate">
              {t("verify.use_resend")}
            </span>
          )}
        </div>
      </div>
      <button
        onClick={() => resend()}
        disabled={!canResend}
        className="inline-flex items-center gap-1.5 rounded-md bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white text-xs font-bold px-3 h-11 min-h-11 sm:h-9 whitespace-nowrap"
        data-testid="email-verify-resend"
      >
        {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
        {cooldown > 0 ? `${t("verify.resend")} (${cooldown}s)` : t("verify.resend")}
      </button>
      <button
        onClick={() => setHidden(true)}
        className="h-9 w-9 shrink-0 rounded-md hover:bg-amber-100 text-amber-700 flex items-center justify-center"
        aria-label={t("verify.hide_aria")}
        data-testid="email-verify-dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
